import { useEffect, useState, useMemo } from 'react'
import notDeleted from '../helpers/notDeleted'
import { definitionCollection } from '../firebase/publicDefinitions';

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Resolves how many MeasureMoments belong to the questionnaire that owns
// an indicator. Used by `useIndicatorCardControls` to pick between the
// `*-moment` and `*-development` visualisation variants.
//
// Two data sources:
//   - LIVE: queries the current tenant's Firestore (cached per QuestionnaireID).
//   - TEMPLATE: derives the count synchronously from the exportTemplateData
//     blob — needed because the template renders the Stichting Template
//     tenant's data while the user is logged into their own tenant, so a
//     Firestore query would return 0.
//
// Both Research (with QuestionnaireID) and MeasureMoments (with ResearchID)
// are already included in the template export.

// Shared cache keyed by QuestionnaireID. Research + MeasureMoments docs change
// rarely (admins manage them), so a one-time fetch is fine.
//
// Cache values:
//   null       → not requested yet
//   Promise    → fetch in flight
//   number (≥0) → resolved momentCount
const cache = new Map()

const fetchMomentCount = async (questionnaireID) => {
    const researchSnap = await definitionCollection('Research')
        .where('QuestionnaireID', '==', questionnaireID)
        .get()
    const researchDoc = researchSnap.docs.filter((d) => notDeleted(d.data()))[0]?.data()
    const researchId = researchDoc?.ID
    if (!researchId) return 0
    const momentsSnap = await definitionCollection('MeasureMoments')
        .where('ResearchID', '==', researchId)
        .get()
    // Count only live moments — soft-deleted ones (ADR-008) stay in Firestore.
    return momentsSnap.docs.filter((d) => !d.data().Deleted).length
}

/**
 * Count MeasureMoments for a questionnaire from a pre-fetched template blob.
 * Returns 0 when the data isn't there.
 *
 * @param {string} questionnaireID
 * @param {object} templateData - The `templateData` object from TemplateDataContext (or `templateData.data`)
 */
const countFromTemplate = (questionnaireID, templateData) => {
    if (!questionnaireID || !templateData) return 0
    // Accept either the full `{ companyId, data, ... }` envelope or the inner
    // `data` blob, since callers thread it through inconsistently.
    const data = templateData.data || templateData
    const research = (data?.research || []).find(r => r.QuestionnaireID === questionnaireID)
    const researchId = research?.ID
    if (!researchId) return 0
    const moments = data?.effects?.measureMoments || []
    return moments.filter(m => m.ResearchID === researchId && !m.Deleted).length
}

/**
 * @param {string} questionnaireID
 * @param {object|null} [templateData] - Optional template blob. When set, skips Firestore.
 */
const useIndicatorMomentCount = (questionnaireID, templateData = null) => {
    const isTemplate = !!templateData

    // Template-mode count is pure-synchronous; memoise on inputs so we don't
    // churn React state when other deps change.
    const templateCount = useMemo(
        () => (isTemplate ? countFromTemplate(questionnaireID, templateData) : 0),
        [isTemplate, questionnaireID, templateData]
    )

    const cached = questionnaireID ? cache.get(questionnaireID) : null
    const [liveCount, setLiveCount] = useState(
        typeof cached === 'number' ? cached : 0
    )

    useEffect(() => {
        if (isTemplate) return
        if (!questionnaireID) {
            setLiveCount(0)
            return
        }

        const existing = cache.get(questionnaireID)
        if (typeof existing === 'number') {
            setLiveCount(existing)
            return
        }

        let cancelled = false
        const promise = existing instanceof Promise
            ? existing
            : fetchMomentCount(questionnaireID)

        if (!(existing instanceof Promise)) {
            cache.set(questionnaireID, promise)
        }

        promise.then((count) => {
            cache.set(questionnaireID, count)
            if (!cancelled) setLiveCount(count)
        })

        return () => {
            cancelled = true
        }
    }, [questionnaireID, isTemplate])

    return isTemplate ? templateCount : liveCount
}

export default useIndicatorMomentCount
